import type { InboxResponse } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { InboxList } from '../components/InboxList.tsx';
import { SubNav } from '../components/SubNav.tsx';
import type { Route } from '../routes.ts';
import { ActionsView } from './ActionsView.tsx';
import { KnowledgeView } from './KnowledgeView.tsx';
import { ReviewQueueView } from './ReviewQueueView.tsx';

/**
 * Everything waiting on a person, in one place.
 *
 * Three queues that used to be three rail buttons, and they are genuinely
 * three: a plan an agent stopped to have approved, a write an agent staged
 * and cannot perform, and a belief a run came away with that would change
 * every future run if accepted. They are one screen because the question
 * that brings somebody here is one question — "is anything waiting on me" —
 * and the badge on the rail is the sum.
 *
 * **All** is the answer to that question and so it is what `/inbox` opens:
 * the three queues as one list, newest first, each row decidable where it
 * sits. The three queues stay as sections behind it, at the paths reef has
 * already sent out, because each has things the union does not — the full
 * plan text, the exact call, the decided history.
 */
export function InboxView({
  section,
  item,
  inbox,
  isAdmin,
  onSection,
  onDecided,
  go,
}: {
  section: string;
  /** The row a link named, to scroll to and mark. */
  item: string | null;
  inbox: InboxResponse | null;
  isAdmin: boolean;
  onSection(id: string): void;
  onDecided(): void;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  const byKind = inbox?.byKind;
  return (
    <div className="screen">
      <SubNav
        title="Inbox"
        current={section}
        onChange={onSection}
        items={[
          { id: 'all', label: 'All', count: inbox?.count ?? 0 },
          { id: 'review', label: 'Review Queue', count: byKind?.review ?? 0 },
          { id: 'approvals', label: 'Approvals', count: byKind?.action ?? 0 },
          { id: 'knowledge', label: 'Knowledge', count: byKind?.knowledge ?? 0 },
        ]}
      />
      {section === 'approvals' ? (
        <ActionsView highlight={item} onChanged={onDecided} />
      ) : section === 'knowledge' ? (
        <KnowledgeView onChanged={onDecided} highlight={item} />
      ) : section === 'review' ? (
        <ReviewQueueView onDecided={onDecided} highlight={item} />
      ) : (
        <InboxAll inbox={inbox} isAdmin={isAdmin} highlight={item} onDecided={onDecided} go={go} />
      )}
    </div>
  );
}

/**
 * The union, as a page. Reads the shell's copy rather than fetching: the
 * shell polls `/inbox` for the badge anyway, and a second fetch here would be
 * a second list to disagree with the first for a moment after a decision.
 */
function InboxAll({
  inbox,
  isAdmin,
  highlight,
  onDecided,
  go,
}: {
  inbox: InboxResponse | null;
  isAdmin: boolean;
  highlight: string | null;
  onDecided(): void;
  go(route: Partial<Route> & { tab: Route['tab'] }): void;
}) {
  return (
    <div className="page inbox-all">
      <header className="page-head">
        <div>
          <h1>Everything waiting on you</h1>
          <p className="page-lede">
            Plans stopped for a decision, writes an agent staged and could not perform, and claims a
            run came away with — newest first. Decide here, or open one in its queue for the whole
            story.
          </p>
        </div>
        <button className="ghost" onClick={onDecided}>
          Refresh
        </button>
      </header>

      {inbox === null ? (
        <p className="hint">Checking what is waiting…</p>
      ) : inbox.items.length === 0 ? (
        <section className="panel">
          <p className="panel-quiet">
            <Icon name="check" /> Nothing is waiting on you.
          </p>
          {inbox.hidden > 0 && <Hidden count={inbox.hidden} />}
        </section>
      ) : (
        <section className="panel">
          <InboxList
            items={inbox.items}
            isAdmin={isAdmin}
            highlight={highlight}
            onDecided={onDecided}
            go={go}
          />
          {inbox.hidden > 0 && <Hidden count={inbox.hidden} />}
        </section>
      )}
    </div>
  );
}

/**
 * Said plainly rather than omitted, as each queue does on its own screen.
 * Somebody whose colleague sees three and who sees none needs to know the
 * difference is permission, not a bug — otherwise this screen gets reported as
 * broken.
 */
function Hidden({ count }: { count: number }) {
  return (
    <p className="hint" style={{ margin: 0 }}>
      {count} {count === 1 ? 'item is' : 'items are'} waiting on workflows you have not been shared
      on.
    </p>
  );
}
